import { authFetch, publicFetch } from "../../jobTemplate/services/httpClient";

const API_BASE_URL = process.env.REACT_APP_REST_BASE_URL;

export const runCode = async ({ language, code, auto_generate, input }) => {
  try {
    const request = {
      language,
      code,
      input: input || "",  // 👈 Send input as a string
      test_cases: [],      // 👈 Send empty test_cases array
      auto_generate,
    };
    const response = await authFetch(`${API_BASE_URL}/compiler/run`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
    const responseJson = await response.json();
    
    return responseJson;
  } catch (error) {
    return { status: "error", stderr: `Error while running: ${error.message}` };
  }
};



export const uploadChunkWithRetry = async (formData, fileName, chunkIndex, retries = 8, backoffBase = 1000) => {
  formData.append("file_name", fileName);
  const sessionId = formData.get("session_id") || "unknown";
  const chunk = chunkIndex !== undefined ? chunkIndex : "?";

  for (let attempt = 0; attempt < retries; attempt++) {
    const attemptNum = attempt + 1;
    // ---------- Change 1: Chunk retry logs ----------
    console.log(
      `📤 CHUNK UPLOAD | session=${sessionId} | chunk=${chunk} | attempt=${attemptNum}/${retries}`,
    );
    try {
      const res = await authFetch(`${API_BASE_URL}/first_camera/upload-chunk/`, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        try {
          const result = await res.json();
          console.log(
            `✅ CHUNK UPLOAD SUCCESS | session=${sessionId} | chunk=${chunk} | attempt=${attemptNum} | block_id=${result.block_id || "n/a"}`,
          );
          return result;
        } catch (e) {
          console.warn("Response not JSON, returning true");
          return true;
        }
      }

      const text = await res.text();
      let detail = null;
      try {
        detail = JSON.parse(text);
      } catch (_) { /* not json */ }

      if (detail?.status === "missing_chunks") {
        console.error(
          `❌ CHUNK UPLOAD FAILED | session=${sessionId} | chunk=${chunk} | attempt=${attemptNum}/${retries} | status=missing_chunks | missing=${detail.missing_chunks || []}`,
        );
      } else {
        console.error(
          `❌ CHUNK UPLOAD FAILED | session=${sessionId} | chunk=${chunk} | attempt=${attemptNum}/${retries} | status=${res.status}`,
        );
      }

      throw new Error(
        detail?.message ||
        detail?.error ||
        `Upload failed with status ${res.status}: ${text}`,
      );
    } catch (err) {
      const isLastAttempt = attempt === retries - 1;
      if (isLastAttempt) {
        throw err;
      }
      const waitMs = backoffBase * Math.pow(2, attempt);
      console.warn(
        `🔄 CHUNK RETRY | session=${sessionId} | chunk=${chunk} | next_attempt=${attempt + 2} | delay=${waitMs}ms`,
      );
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
  }
};

// api.js
export const finalizeUpload = async ({ sessionId, fileName, totalChunks }) => {
  if (!sessionId) throw new Error("Missing sessionId for finalizeUpload");
  if (!fileName) throw new Error("Missing fileName for finalizeUpload");
  const expectedChunks = Number(totalChunks);
  if (!Number.isSafeInteger(expectedChunks) || expectedChunks < 1) {
    throw new Error("finalizeUpload requires a positive integer totalChunks");
  }

  const requestPayload = {
    session_id: sessionId,
    file_name: fileName,
    total_chunks: expectedChunks,
  };

  // ---------- Fix 6: 3-attempt transient retry (skip 409 missing_chunks) ----------
  const retries = 3;
  const backoffBase = 1000;

  for (let attempt = 0; attempt < retries; attempt++) {
    const attemptNum = attempt + 1;

    // Only log REQUEST on first attempt; for retries log RETRY
    if (attempt === 0) {
      console.log(
        `🎬 FINALIZE REQUEST | session=${sessionId} | chunks=${totalChunks}`,
      );
    }

    try {
      const res = await authFetch(`${API_BASE_URL}/first_camera/finalize-upload/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let detail = null;
        try {
          detail = JSON.parse(errorText);
        } catch (_) { /* not json */ }

        // Handle 409 missing_chunks: NEVER retry a deterministic missing-chunk response
        if (detail?.detail?.status === "missing_chunks") {
          const missing = detail.detail.missing_chunks || [];
          console.error(
            `❌ RECORDING FINALIZATION BLOCKED | session=${sessionId} | missing chunks = ${missing.join(",")}`,
          );
          return {
            status: "missing_chunks",
            missing_chunks: missing,
            message: detail.detail.message,
          };
        }

        // Decide if this error is retryable
        const retryable = (
          res.status === 500 ||
          res.status === 502 ||
          res.status === 503 ||
          res.status === 504 ||
          (res.status >= 400 && (
            (detail?.detail && typeof detail.detail === "string" && detail.detail.startsWith("commit_failed")) ||
            false
          ))
        );

        if (retryable && attempt < retries - 1) {
          const waitMs = backoffBase * Math.pow(2, attempt);
          console.warn(
            `🔄 FINALIZE RETRY | session=${sessionId} | attempt=${attemptNum}/${retries} | status=${res.status} | next_attempt=${attempt + 2} | delay=${waitMs}ms`,
          );
          await new Promise((resolve) => setTimeout(resolve, waitMs));
          continue;
        }

        if (detail?.detail) {
          console.error(
            `🚨 FINALIZE FAILED | session=${sessionId} | attempt=${attemptNum}/${retries} | status=${res.status} | error=${JSON.stringify(detail.detail)}`,
          );
          throw new Error(
            `Finalize failed: ${res.status} - ${
              typeof detail.detail === "string"
                ? detail.detail
                : detail.detail?.message || errorText
            }`,
          );
        }

        console.error(
          `🚨 FINALIZE FAILED | session=${sessionId} | attempt=${attemptNum}/${retries} | status=${res.status} | error=${errorText}`,
        );
        throw new Error(`Finalize failed: ${res.status} - ${errorText}`);
      }

      const data = await res.json();

      if (data.status === "processing") {
        console.log(
          `✅ FINALIZE SUCCESS | session=${sessionId} | status=processing | attempt=${attemptNum}`,
        );
      } else if (data.status === "ready") {
        console.log(
          `✅ FINALIZE SUCCESS | session=${sessionId} | status=ready | attempt=${attemptNum}`,
        );
      } else {
        console.log(
          `✅ FINALIZE SUCCESS | session=${sessionId} | status=${data.status || "unknown"} | attempt=${attemptNum}`,
        );
      }

      return data;

    } catch (err) {
      const isLastAttempt = attempt === retries - 1;

      // Network-layer errors (no response) are always retryable
      const networkError = !err.status && (
        err instanceof TypeError ||
        err.message?.toLowerCase().includes("network") ||
        err.message?.toLowerCase().includes("fetch") ||
        err.name === "AbortError"
      );

      if (networkError && !isLastAttempt) {
        const waitMs = backoffBase * Math.pow(2, attempt);
        console.warn(
          `🔄 FINALIZE RETRY (network) | session=${sessionId} | attempt=${attempt + 1}/${retries} | error=${err.message || err} | delay=${waitMs}ms`,
        );
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        continue;
      }

      if (isLastAttempt) {
        console.error("Finalize upload error:", err);
        throw err;
      }
      console.warn(
        `Finalize non-retryable error on attempt ${attempt + 1}:`,
        err,
      );
      throw err;
    }
  }

  // Unreachable: loop ends with return or throw
  throw new Error("Finalize retry exhausted");
};

export const fetchInterviewDetails = async (interviewId, { strict = false } = {}) => {
  if (!interviewId) throw new Error("Missing interviewId");
  try {
    const res = await publicFetch(
      `${API_BASE_URL}/interviews/${interviewId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      }
    );

    if (!res.ok) {
      console.warn(`Failed to fetch interview details: ${res.status}`);
      if (strict) throw new Error(`Failed to fetch interview details: ${res.status}`);
      return null;
    }

    const data = await res.json();
    return data;
  } catch (err) {
    console.error("Error fetching interview details:", err);
    if (strict) throw err;
    return null;
  }
};

export const validateInterviewLink = async (interviewId) => {
  if (!interviewId) throw new Error("Missing interviewId");
  try {
    const res = await publicFetch(
      `${API_BASE_URL}/validate-interview-link/${interviewId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      }
    );

    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { raw: text };
    }

    if (res.ok) {
      return {
        ok: true,
        status: "valid",
        message: data.message || "Interview link is valid",
        scheduled_date: data.scheduled_date || null,
        scheduled_time: data.scheduled_time || null,
        interview_timezone: data.interview_timezone || null,
        interview_duration: data.interview_duration || 30,
        candidate_name: data.candidate_name || "Unknown Candidate",
      };
    } else if (res.status === 403) {
      return {
        ok: false,
        status: "forbidden",
        message: data.detail?.error || "Interview not allowed",
        scheduled_date: data.detail?.["Interview Scheduled Date"] || null,
        scheduled_time: data.detail?.["Interview Scheduled Time"] || null,
        interview_timezone: data.detail?.["Interview Timezone"] || null,
        interview_duration: 30,
        candidate_name: null,
      };
    } else {
      return {
        ok: false,
        status: "error",
        message: data.message || data.error || data.raw || `HTTP ${res.status}`,
        scheduled_date: null,
        scheduled_time: null,
        interview_timezone: null,
        interview_duration: 30,
        candidate_name: null,
      };
    }
  } catch (err) {
    return {
      ok: false,
      status: "error",
      message: err.message,
      scheduled_date: null,
      scheduled_time: null,
      interview_timezone: null,
      interview_duration: 30,
      candidate_name: null,
    };
  }
};

export const checkSecondCameraInterviewStatus = async (interviewId) => {
  try {
    const res = await publicFetch(
      `${API_BASE_URL}/interview_ai/check-second-camera-status/${interviewId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      }
    );

    if (!res.ok) {
      console.warn(`Failed to check interview status: ${res.status}`);
      return { ended: false, error: true };
    }

    const data = await res.json();
    return {
      ended: data.second_camera_inactive === true,
      error: false,
      status: data,
    };
  } catch (err) {
    console.error("Error checking interview status:", err);
    return { ended: false, error: true };
  }
};

export const fetchRandomCodingQuestion = async (interviewId) => {
  try {
    if (!interviewId) {
      throw new Error("interviewId is required");
    }

    const response = await publicFetch(
      `${API_BASE_URL}/code_evaluation/coding-questions/random?interviewId=${interviewId}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch coding question: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching random coding question:", error);
    throw error;
  }
};

export const sendFrameToBackend = async ({ videoRef, canvasRef, sessionId }) => {
  if (!videoRef.current || !canvasRef.current) return;

  const interviewId = localStorage.getItem("interviewId");
  if (!interviewId) return;

  const ctx = canvasRef.current.getContext("2d");
  ctx.drawImage(videoRef.current, 0, 0, 320, 240);

  return new Promise((resolve) => {
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return resolve(null);

      const formData = new FormData();
      formData.append("file", blob, "frame.jpg");
      formData.append("session_id", sessionId);

      try {
        const res = await authFetch(
          `${API_BASE_URL}/video_proctoring/upload-frame?session_id=${interviewId}`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await res.json();

        // Return full event object if present
        if (data.event) {
          return resolve({
            event: true,
            eventData: data.event,
            message: `${data.event.event_type}`,
          });
        }

        return resolve({ event: false });

      } catch (err) {
        console.error("Frame send error:", err);
        resolve({ event: false });
      }
    }, "image/jpeg");
  });
};

export const downloadSystemCheckExe = async () => {
  try {
    const baseUrl = API_BASE_URL || "http://localhost:8000";
    const res = await authFetch(`${baseUrl}/system_check/download-exe`, {
      method: "GET",
    });

    if (!res.ok) {
      throw new Error(`Download exe failed: HTTP ${res.status}`);
    }

    return res.blob();
  } catch (err) {
    console.error("System check exe download error:", err);
    throw err;
  }
};

export const downloadSystemCheckBat = async (interviewId) => {
  try {
    if (!interviewId) {
      throw new Error("Interview ID is required to download bat file");
    }

    const baseUrl = API_BASE_URL || "http://localhost:8000";
    const res = await authFetch(
      `${baseUrl}/system_check/download-system-check-bat?interview_id=${encodeURIComponent(interviewId)}`,
      {
        method: "GET",
      }
    );

    if (!res.ok) {
      throw new Error(`Download bat failed: HTTP ${res.status}`);
    }

    return res.blob();
  } catch (err) {
    console.error("System check bat download error:", err);
    throw err;
  }
};

export const checkSystemCheckStatus = async (interviewId) => {
  try {
    if (!interviewId) {
      throw new Error("Interview ID is required to check status");
    }

    const baseUrl = API_BASE_URL || "http://localhost:8000";
    const res = await authFetch(`${baseUrl}/system_check/check-status/${encodeURIComponent(interviewId)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    return data;
  } catch (err) {
    console.error("System check status error:", err);
    return null;
  }
};
